import React, {useEffect, useMemo, useState} from "react";
import {injectIntl} from "react-intl";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Select, {SelectOption} from "@amzn/meridian/select";
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert";
import Tooltip from "@amzn/meridian/tooltip";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import IntervalInput, {isValidInterval} from "../../commonui/IntervalInput";
import useSequenceRunner from "../../util/useSequenceRunner";
import {chainWalk} from "../../util/Util";
import {getLocalizedMessage} from "../../util/Localization";
import {
    PICK_EXECUTION_DEFAULT_INTERVAL_SECONDS,
    PICK_EXECUTION_MAX_INTERVAL_SECONDS,
    PICK_EXECUTION_MIN_HIGHLIGHT_MS,
    PICK_EXECUTION_ROW_COLOR,
    PICK_EXECUTION_STATUS_TEXT_COLOR,
    SEQUENCE_ITEM_STATUS,
    SEQUENCE_RUN_STATE
} from "../../util/Constants";
import {publishMockPickEvent} from "./network/AutoRetrieverDeviceNetworkAPI";

const NO_ITEMS = [];
const PROGRESS_MESSAGE = {
    [SEQUENCE_RUN_STATE.PAUSED]: "pickexecution.progress_paused",
    [SEQUENCE_RUN_STATE.STOPPED]: "pickexecution.progress_stopped"
};

/**
 * Converts the testConfigForAR picklists into picklists with their items in pick order.
 * Items are grouped by cluster in testConfigForAR, so the cluster is copied onto each item.
 */
export const getPicklistsForExecution = (testConfig) => {
    return chainWalk(() => testConfig.picklists, []).map(picklist => ({
        key: picklist.id + "#" + picklist.groupId,
        id: picklist.id,
        groupId: picklist.groupId,
        pickPoint: picklist.retrievalLocation,
        items: Object.keys(picklist.pickItems || {}).reduce((items, cluster) =>
            items.concat(picklist.pickItems[cluster].map(item => ({id: item.id, type: item.type, cluster}))), [])
    }));
};

/**
 * Pick Test view to execute a mock picklist: sends one mock PICK event per item, waiting the selected interval
 * between two items. The run lives in this view only, so it stops when the view is closed.
 */
const PickTestExecution = ({intl, selectedMac, testConfig}) => {
    const [pickPoint, setPickPoint] = useState("");
    const [picklistKey, setPicklistKey] = useState("");
    const [intervalSeconds, setIntervalSeconds] = useState(String(PICK_EXECUTION_DEFAULT_INTERVAL_SECONDS));

    // The device data reloads periodically with a new object, so only a new timestamp means new picklists
    const testConfigTimestamp = chainWalk(() => testConfig.timestamp, null);
    const picklists = useMemo(() => getPicklistsForExecution(testConfig), [testConfigTimestamp]);

    useEffect(() => {
        setPickPoint("");
        setPicklistKey("");
    }, [testConfigTimestamp]);

    const pickPoints = [...new Set(picklists.map(picklist => picklist.pickPoint))].sort();
    const picklistsForPickPoint = picklists.filter(picklist => picklist.pickPoint === pickPoint);
    const selectedPicklist = picklists.find(picklist => picklist.key === picklistKey);
    const items = selectedPicklist ? selectedPicklist.items : NO_ITEMS;

    const sendPick = (item) => publishMockPickEvent({
        macAddress: selectedMac,
        itemId: item.id,
        itemType: item.type,
        cluster: item.cluster,
        picklistId: selectedPicklist.id,
        groupId: selectedPicklist.groupId,
        pickPoint: selectedPicklist.pickPoint
    });

    const {statuses, runState, isItemInProgress, start, pause, resume, stop} = useSequenceRunner({
        items,
        intervalMs: Number(intervalSeconds) * 1000,
        process: sendPick,
        minInProgressMs: PICK_EXECUTION_MIN_HIGHLIGHT_MS
    });

    const isIntervalValid = isValidInterval(intervalSeconds, 0, PICK_EXECUTION_MAX_INTERVAL_SECONDS);
    const sentCount = statuses.filter(status => status === SEQUENCE_ITEM_STATUS.COMPLETED).length;
    const failedIndex = statuses.indexOf(SEQUENCE_ITEM_STATUS.FAILED);
    const isRunning = runState === SEQUENCE_RUN_STATE.RUNNING;
    const canContinue = runState === SEQUENCE_RUN_STATE.PAUSED || runState === SEQUENCE_RUN_STATE.FAILED;
    const isRunActive = isRunning || canContinue;
    const getStatus = (index) => statuses[index] || SEQUENCE_ITEM_STATUS.PENDING;

    if (!picklists.length) {
        return (
            <Alert type="informational" size="small">
                {getLocalizedMessage({intl, stringId: "pickexecution.no_picklists"})}
            </Alert>
        );
    }

    return (
        <Column spacing="400">
            <Row spacing="400" alignmentVertical="top" wrap="down">
                <Row spacing="200">
                    <Text>{getLocalizedMessage({intl, stringId: "testconfig.pick_point"})}</Text>
                    <Select
                        id="pick-execution-pick-point"
                        value={pickPoint}
                        onChange={(value) => {
                            setPickPoint(value);
                            setPicklistKey("");
                        }}
                        disabled={isRunning}
                        placeholder={getLocalizedMessage({intl, stringId: "pickexecution.select_pick_point"})}
                        size="small"
                        width={180}>
                        {pickPoints.map(point => <SelectOption key={point} value={point} label={point}/>)}
                    </Select>
                </Row>
                <Row spacing="200">
                    <Text>{getLocalizedMessage({intl, stringId: "pickexecution.picklist"})}</Text>
                    <Select
                        id="pick-execution-picklist"
                        value={picklistKey}
                        onChange={setPicklistKey}
                        disabled={isRunning || !pickPoint}
                        placeholder={getLocalizedMessage({intl, stringId: "pickexecution.select_picklist"})}
                        size="small"
                        width={250}>
                        {picklistsForPickPoint.map(picklist =>
                            <SelectOption key={picklist.key} value={picklist.key}
                                          label={getLocalizedMessage({
                                              intl, stringId: "pickexecution.picklist_option",
                                              values: {picklist: picklist.key, count: picklist.items.length}
                                          })}/>)}
                    </Select>
                </Row>
                <Row spacing="200" alignmentVertical="top">
                    <Text>{getLocalizedMessage({intl, stringId: "common.interval"})}</Text>
                    <IntervalInput
                        id="pick-execution-interval"
                        value={intervalSeconds}
                        onChange={setIntervalSeconds}
                        max={PICK_EXECUTION_MAX_INTERVAL_SECONDS}
                        unit={getLocalizedMessage({intl, stringId: "common.seconds"})}
                        errorMessage={getLocalizedMessage({
                            intl, stringId: "common.interval_error",
                            values: {min: 0, max: PICK_EXECUTION_MAX_INTERVAL_SECONDS}
                        })}
                        disabled={isRunning}/>
                </Row>
                <Row spacing="200">
                    <Button id="pick-execution-start" type="primary" size="small" className="greenBtn" onClick={start}
                            disabled={runState !== SEQUENCE_RUN_STATE.IDLE || !selectedPicklist || !isIntervalValid}>
                        {getLocalizedMessage({intl, stringId: "pickexecution.start"})}
                    </Button>
                    {canContinue ?
                        <Button id="pick-execution-continue" type="primary" size="small" className="greenBtn" onClick={resume}
                                disabled={isItemInProgress || !isIntervalValid}>
                            {getLocalizedMessage({intl, stringId: "pickexecution.continue"})}
                        </Button> :
                        <Button id="pick-execution-pause" type="primary" size="small" className="yellowBtn" onClick={pause}
                                disabled={!isRunning}>
                            {getLocalizedMessage({intl, stringId: "pickexecution.pause"})}
                        </Button>
                    }
                    <Button id="pick-execution-stop" type="primary" size="small" className="redBtn" onClick={stop}
                            disabled={!isRunActive}>
                        {getLocalizedMessage({intl, stringId: "common.stop"})}
                    </Button>
                </Row>
            </Row>
            {isRunActive &&
                <Alert type="informational" size="small">
                    {getLocalizedMessage({intl, stringId: "pickexecution.keep_open"})}
                </Alert>
            }
            {canContinue && failedIndex >= 0 &&
                <Alert type="error" size="small">
                    {getLocalizedMessage({
                        intl, stringId: "pickexecution.failed", values: {itemId: items[failedIndex].id}
                    })}
                </Alert>
            }
            {runState === SEQUENCE_RUN_STATE.COMPLETED &&
                <Alert type="success" size="small">
                    {getLocalizedMessage({
                        intl, stringId: "pickexecution.completed",
                        values: {count: items.length, picklist: selectedPicklist.key}
                    })}
                </Alert>
            }
            {selectedPicklist &&
                <Column spacing="200">
                    <Text type="b200">
                        {getLocalizedMessage({
                            intl, stringId: PROGRESS_MESSAGE[runState] || "pickexecution.progress",
                            values: {sent: sentCount, total: items.length}
                        })}
                    </Text>
                    <Table headerRows={1} spacing="small" showDividers={true}>
                        <TableRow highlightOnHover={false}>
                            <TableCell width={80}>{getLocalizedMessage({intl, stringId: "table.index"})}</TableCell>
                            <TableCell>{getLocalizedMessage({intl, stringId: "testconfig.pick_item"})}</TableCell>
                            <TableCell>{getLocalizedMessage({intl, stringId: "testconfig.pick_item_type"})}</TableCell>
                            <TableCell>{getLocalizedMessage({intl, stringId: "testconfig.cluster"})}</TableCell>
                            <TableCell>
                                <Tooltip position="top"
                                         title={getLocalizedMessage({intl, stringId: "pickexecution.status_tooltip"})}>
                                    <span>{getLocalizedMessage({intl, stringId: "common.status"})}</span>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                        {items.map((item, index) => (
                            <TableRow key={index + "-" + item.id} backgroundColor={PICK_EXECUTION_ROW_COLOR[getStatus(index)]}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>{item.id}</TableCell>
                                <TableCell>{item.type}</TableCell>
                                <TableCell>{item.cluster}</TableCell>
                                <TableCell>
                                    <Text color={PICK_EXECUTION_STATUS_TEXT_COLOR[getStatus(index)]}>
                                        {getLocalizedMessage({
                                            intl, stringId: "pickexecution.status." + getStatus(index)
                                        })}
                                    </Text>
                                </TableCell>
                            </TableRow>
                        ))}
                    </Table>
                </Column>
            }
        </Column>
    );
};

export default injectIntl(PickTestExecution);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/autoretriever/PickTestExecution.js